import React, { useState } from 'react';
import './ResultCard.css';

// ─── Config maps ──────────────────────────────────────────────────────────────

const CONFIDENCE_CFG = {
  high:   { label: 'High Confidence',  color: 'var(--high)',   bg: 'var(--high-bg)',   icon: '◉' },
  good:   { label: 'Good',             color: 'var(--good)',   bg: 'var(--good-bg)',   icon: '◎' },
  review: { label: 'Review',           color: 'var(--review)', bg: 'var(--review-bg)', icon: '◌' },
  low:    { label: 'Low Confidence',   color: 'var(--low)',    bg: 'var(--low-bg)',    icon: '○' },
};

const REC_CFG = {
  HIGHLY_RECOMMENDED: { label: 'Highly Recommended', color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   icon: '✓✓' },
  RECOMMENDED:        { label: 'Recommended',         color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  icon: '✓' },
  REVIEW:             { label: 'Review',               color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: '?' },
  DO_NOT_SEND:        { label: 'Do Not Send',          color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: '✗' },
};

const RISK_CFG = {
  LOW:     { label: 'Low Risk',     color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  MEDIUM:  { label: 'Medium Risk',  color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  HIGH:    { label: 'High Risk',    color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  UNKNOWN: { label: 'Unknown Risk', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
};

const SMTP_STATE_LABELS = {
  ACCEPTED:           { label: 'Accepted',            color: '#22c55e' },
  REJECTED:           { label: 'Rejected',            color: '#ef4444' },
  TIMEOUT:            { label: 'Timeout',             color: '#f59e0b' },
  CONNECTION_REFUSED: { label: 'Connection Refused',  color: '#f59e0b' },
  BLOCKED:            { label: 'Blocked',             color: '#f59e0b' },
  INCONCLUSIVE:       { label: 'Inconclusive',        color: '#94a3b8' },
  NOT_CHECKED:        { label: 'Not Checked',         color: '#64748b' },
  ERROR:              { label: 'Error',               color: '#ef4444' },
};

const CATCH_ALL_LABELS = {
  YES:          { label: 'Yes — Catch-all',     color: '#f59e0b' },
  NO:           { label: 'No',                  color: '#22c55e' },
  INCONCLUSIVE: { label: 'Inconclusive',        color: '#94a3b8' },
  NOT_CHECKED:  { label: 'Not Checked',         color: '#64748b' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreRing({ score, color }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="score-ring-wrap">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="var(--surface3)" strokeWidth="6" />
        <circle
          cx="36" cy="36" r={r} fill="none"
          stroke={color} strokeWidth="6"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="score-ring-label" style={{ color }}>{score}</div>
    </div>
  );
}

function CheckRow({ label, passed, neutral, details, tooltip }) {
  const [open, setOpen] = useState(false);
  const hasDetails = details && details.filter(Boolean).length > 0;
  return (
    <div className={`check-row ${neutral ? 'neutral' : passed ? 'pass' : 'fail'}`}>
      <div className="check-header" onClick={() => hasDetails && setOpen(o => !o)} style={{ cursor: hasDetails ? 'pointer' : 'default' }}>
        <span className="check-dot-icon">{neutral ? '–' : passed ? '✓' : '✗'}</span>
        <span className="check-label">{label}</span>
        {tooltip && <span className="check-tooltip" title={tooltip}>ⓘ</span>}
        {hasDetails && <span className="check-toggle">{open ? '▲' : '▼'}</span>}
      </div>
      {open && hasDetails && (
        <ul className="check-details-list">
          {details.filter(Boolean).map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
    </div>
  );
}

function InfoRow({ label, value, valueColor, sub }) {
  return (
    <div className="info-row">
      <span className="info-label">{label}</span>
      <span className="info-value" style={valueColor ? { color: valueColor } : {}}>{value}</span>
      {sub && <span className="info-sub">{sub}</span>}
    </div>
  );
}

// ─── Main ResultCard ──────────────────────────────────────────────────────────

const CERTAINTY_CFG = {
  VERIFIED:          { label: 'Verified',          color: '#22c55e' },
  MOSTLY_VERIFIED:   { label: 'Mostly Verified',   color: '#4ade80' },
  INCONCLUSIVE:      { label: 'Inconclusive',      color: '#94a3b8' },
  UNVERIFIED:        { label: 'Unverified',        color: '#f59e0b' },
  VERIFIED_NEGATIVE: { label: 'Verified Negative', color: '#ef4444' },
};

export default function ResultCard({ result }) {
  const [techOpen, setTechOpen] = useState(false);

  const {
    email, checks = {},
    confidenceScore = 0, confidenceLevel = 'low',
    recommendation = 'REVIEW', recommendationLabel, recommendationReason,
    spamRisk = 'UNKNOWN', spamRiskReason,
    smtpState = 'NOT_CHECKED', catchAllState = 'NOT_CHECKED',
    verificationCertainty = 'UNVERIFIED', verificationCertaintyLabel,
    verdict
  } = result;

  const confCfg = CONFIDENCE_CFG[confidenceLevel] || CONFIDENCE_CFG.low;
  const recCfg = REC_CFG[recommendation] || REC_CFG.REVIEW;
  const riskCfg = RISK_CFG[spamRisk] || RISK_CFG.UNKNOWN;
  const smtpLabel = SMTP_STATE_LABELS[smtpState] || SMTP_STATE_LABELS.NOT_CHECKED;
  const catchLabel = CATCH_ALL_LABELS[catchAllState] || CATCH_ALL_LABELS.NOT_CHECKED;
  const certaintyCfg = CERTAINTY_CFG[verificationCertainty] || CERTAINTY_CFG.UNVERIFIED;

  const { syntax, dns, disposable, smtp } = checks;

  return (
    <div className="result-card">
      {/* ── Top banner ── */}
      <div className="rc-banner">
        <div className="rc-banner-left">
          <ScoreRing score={confidenceScore} color={confCfg.color} />
          <div className="rc-banner-info">
            <div className="rc-email">{email}</div>
            <div className="rc-conf-level" style={{ color: confCfg.color }}>
              {confCfg.icon} {confCfg.label}
            </div>
            <div className="rc-verdict">{verdict}</div>
          </div>
        </div>
        <div className="rc-banner-right">
          <div className="rc-rec-badge" style={{ background: recCfg.bg, borderColor: recCfg.color }}>
            <span className="rc-rec-icon" style={{ color: recCfg.color }}>{recCfg.icon}</span>
            <div>
              <div className="rc-rec-label" style={{ color: recCfg.color }}>{recCfg.label}</div>
              <div className="rc-rec-reason">{recommendationReason}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Quality summary grid ── */}
      <div className="rc-quality-grid rc-quality-grid--5">
        <div className="rc-quality-card">
          <div className="rcq-label">Verification</div>
          <div className="rcq-value" style={{ color: certaintyCfg.color }}>
            {verificationCertaintyLabel || certaintyCfg.label}
          </div>
          <div className="rcq-sub">
            {smtpState === 'ACCEPTED' && catchAllState === 'NO' ? 'Mailbox confirmed' :
             smtpState === 'ACCEPTED' ? 'Server accepted' :
             smtpState === 'REJECTED' ? 'Mailbox rejected' :
             smtpState === 'TIMEOUT' || smtpState === 'BLOCKED' ? 'SMTP inconclusive' :
             'Not verified'}
          </div>
        </div>
        <div className="rc-quality-card">
          <div className="rcq-label">Deliverability Risk</div>
          <div className="rcq-value" style={{ color: riskCfg.color }}>{riskCfg.label}</div>
          <div className="rcq-sub">{spamRiskReason}</div>
        </div>
        <div className="rc-quality-card">
          <div className="rcq-label">Domain Health</div>
          <div className="rcq-value" style={{ color: dns?.hasMX ? 'var(--valid)' : 'var(--invalid)' }}>
            {dns?.hasMX ? 'Healthy' : 'No MX Records'}
          </div>
          <div className="rcq-sub">
            {dns?.hasSPF && dns?.hasDMARC ? 'SPF + DMARC present' :
             dns?.hasSPF ? 'SPF present, no DMARC' :
             dns?.hasDMARC ? 'DMARC present, no SPF' : 'No SPF or DMARC'}
          </div>
        </div>
        <div className="rc-quality-card">
          <div className="rcq-label">Disposable</div>
          <div className="rcq-value" style={{ color: disposable?.isDisposable ? 'var(--invalid)' : 'var(--valid)' }}>
            {disposable?.isDisposable ? 'Disposable' : 'Not Disposable'}
          </div>
          <div className="rcq-sub">{disposable?.isRoleBased ? 'Role-based address' : 'Personal address'}</div>
        </div>
        <div className="rc-quality-card">
          <div className="rcq-label">Catch-all</div>
          <div className="rcq-value" style={{ color: catchLabel.color }}>{catchLabel.label}</div>
          <div className="rcq-sub">
            {catchAllState === 'YES' ? 'Accepts all addresses' :
             catchAllState === 'NO' ? 'Mailbox confirmed' :
             catchAllState === 'INCONCLUSIVE' ? 'Could not determine' : 'Not checked'}
          </div>
        </div>
      </div>

      {/* ── Checks grid ── */}
      <div className="rc-checks-section">
        <div className="rc-section-title">Validation Checks</div>
        <div className="rc-checks-grid">
          <CheckRow
            label="Syntax"
            passed={syntax?.valid}
            details={syntax?.details}
            tooltip="RFC 5322 email format validation"
          />
          <CheckRow
            label="DNS / MX Records"
            passed={dns?.hasMX}
            details={[
              ...( dns?.details || []),
              ...(dns?.mxRecords?.slice(0,3).map(r => `MX: ${r.exchange} (priority ${r.priority})`) || [])
            ]}
            tooltip="Domain must have MX records to receive email"
          />
          <CheckRow
            label="SPF Record"
            passed={dns?.hasSPF}
            neutral={!dns?.hasMX}
            details={[dns?.spfRecord || (dns?.hasSPF ? 'SPF record found' : 'No SPF record found')]}
            tooltip="SPF helps prevent email spoofing. Missing SPF is a risk signal, not a hard failure."
          />
          <CheckRow
            label="DMARC Record"
            passed={dns?.hasDMARC}
            neutral={!dns?.hasMX}
            details={[dns?.dmarcRecord || (dns?.hasDMARC ? 'DMARC record found' : 'No DMARC record found')]}
            tooltip="DMARC policy helps protect the domain from abuse. Missing DMARC is a risk signal."
          />
          <CheckRow
            label="Not Disposable"
            passed={!disposable?.isDisposable}
            details={disposable?.details?.filter(d => d.includes('disposable') || d.includes('Disposable'))}
            tooltip="Disposable email services provide temporary inboxes"
          />
          <CheckRow
            label="Not Role-Based"
            passed={!disposable?.isRoleBased}
            details={disposable?.details?.filter(d => d.includes('role') || d.includes('Role'))}
            tooltip="Role-based addresses (admin@, info@, etc.) are shared mailboxes"
          />
          <CheckRow
            label="SMTP Probe"
            passed={smtpState === 'ACCEPTED'}
            neutral={smtpState === 'NOT_CHECKED' || smtpState === 'TIMEOUT' || smtpState === 'BLOCKED' || smtpState === 'CONNECTION_REFUSED' || smtpState === 'INCONCLUSIVE'}
            details={[
              `State: ${smtpLabel.label}`,
              ...(smtp?.details || []),
              smtp?.responseCode ? `Response: ${smtp.responseCode} ${smtp.responseMessage}` : null
            ].filter(Boolean)}
            tooltip="SMTP probe connects to the mail server to test if the mailbox accepts mail. Timeout/blocked = inconclusive, not invalid."
          />
          <CheckRow
            label="Not Catch-All"
            passed={catchAllState === 'NO'}
            neutral={catchAllState === 'INCONCLUSIVE' || catchAllState === 'NOT_CHECKED'}
            details={[
              `Catch-all: ${catchLabel.label}`,
              catchAllState === 'YES' ? 'This server accepts all addresses — individual mailbox existence cannot be confirmed.' : null,
              catchAllState === 'INCONCLUSIVE' ? 'Could not determine catch-all status.' : null
            ].filter(Boolean)}
            tooltip="Catch-all domains accept mail for any address, making individual mailbox verification impossible"
          />

        </div>
      </div>

      {/* ── Technical details (expandable) ── */}
      <div className="rc-tech-section">
        <button className="rc-tech-toggle" onClick={() => setTechOpen(o => !o)}>
          <span>Technical Details</span>
          <span>{techOpen ? '▲' : '▼'}</span>
        </button>
        {techOpen && (
          <div className="rc-tech-body">
            <div className="rc-tech-grid">
              <InfoRow label="Email" value={email} />
              <InfoRow label="Domain" value={dns?.domain || '—'} />
              <InfoRow label="Confidence Score" value={`${confidenceScore}/100`} valueColor={confCfg.color} />
              <InfoRow label="Confidence Level" value={confCfg.label} valueColor={confCfg.color} />
              <InfoRow label="Send Recommendation" value={recommendationLabel || recommendation} valueColor={recCfg.color} />
              <InfoRow label="Deliverability Risk" value={riskCfg.label} valueColor={riskCfg.color} />
              <InfoRow label="SMTP State" value={smtpLabel.label} valueColor={smtpLabel.color} />
              <InfoRow label="Catch-all" value={catchLabel.label} valueColor={catchLabel.color} />
              <InfoRow label="MX Records" value={dns?.mxRecords?.length ? `${dns.mxRecords.length} found` : 'None'} />
              <InfoRow label="SPF" value={dns?.hasSPF ? 'Present' : 'Not found'} valueColor={dns?.hasSPF ? 'var(--valid)' : 'var(--text-muted)'} />
              <InfoRow label="DMARC" value={dns?.hasDMARC ? 'Present' : 'Not found'} valueColor={dns?.hasDMARC ? 'var(--valid)' : 'var(--text-muted)'} />

              <InfoRow label="Disposable" value={disposable?.isDisposable ? 'Yes' : 'No'} valueColor={disposable?.isDisposable ? 'var(--invalid)' : 'var(--valid)'} />
              <InfoRow label="Role-based" value={disposable?.isRoleBased ? 'Yes' : 'No'} valueColor={disposable?.isRoleBased ? 'var(--risky)' : 'var(--valid)'} />
            </div>
            {dns?.spfRecord && (
              <div className="rc-tech-record">
                <span className="rc-tech-record-label">SPF Record:</span>
                <code>{dns.spfRecord}</code>
              </div>
            )}
            {dns?.dmarcRecord && (
              <div className="rc-tech-record">
                <span className="rc-tech-record-label">DMARC Record:</span>
                <code>{dns.dmarcRecord}</code>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rc-disclaimer">
        ⓘ This is a technical assessment based on observable signals. It does not guarantee deliverability or confirm mailbox ownership.
      </div>
    </div>
  );
}
